import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import ProductCard from "@/components/ProductCard";
import { fetchCollection, mapShopifyProduct } from "@/lib/shopify";

const COLLECTION_META = {
  swimwear: { section: "WEAR", label: "Swimwear" },
  tops: { section: "WEAR", label: "Tops" },
  bottoms: { section: "WEAR", label: "Bottoms" },
  shapewear: { section: "WEAR", label: "Shapewear" },
  accessories: { section: "WEAR", label: "Accessories" },
  shoes: { section: "WEAR", label: "Shoes" },
  kids: { section: "WEAR", label: "Kids" },
  "bras-underwear": { section: "WEAR", label: "Bras & Underwear" },
  "sleepwear-loungewear": { section: "WEAR", label: "Sleepwear & Loungewear" },
  "home-decor": { section: "BIG LIVING", label: "Home & Decor" },
  drinkware: { section: "BIG LIVING", label: "Drinkware" },
  "bags-travel": { section: "BIG LIVING", label: "Bags & Travel" },
  stationery: { section: "BIG LIVING", label: "Stationery" },
};

export default function CollectionPage() {
  const { handle } = useParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const meta = COLLECTION_META[handle] || { section: "", label: handle };
  const pageTitle = meta.section ? `${meta.section} — ${meta.label}` : meta.label;

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      const result = await fetchCollection(handle, 250);
      if (!cancelled) {
        setProducts((result.products || []).map(mapShopifyProduct));
        setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [handle]);

  return (
    <div className="min-h-screen pt-28 pb-20" data-testid="collection-page">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-plum-light hover:text-plum mb-8 transition-colors duration-200"
          data-testid="collection-back"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to home
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-10 lg:mb-14"
        >
          <h1 className="font-serif text-4xl lg:text-5xl font-bold text-plum tracking-tight">
            {pageTitle}
          </h1>
          {!loading && (
            <p className="mt-2 text-sm text-plum-muted">
              {products.length} product{products.length !== 1 ? "s" : ""}
            </p>
          )}
        </motion.div>

        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-plum/20 border-t-plum rounded-full animate-spin" />
          </div>
        )}

        {!loading && products.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5 lg:gap-8" data-testid="collection-grid">
            {products.map((product, i) => (
              <ProductCard key={product.id} product={product} index={i} />
            ))}
          </div>
        )}

        {!loading && products.length === 0 && (
          <div className="text-center py-20">
            <p className="text-plum-light">No products found in this collection.</p>
          </div>
        )}
      </div>
    </div>
  );
}
